import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { Button, Text, TextInput } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useJoinGroup } from '@/features/api/hooks/grapevine-group-events/useJoinGroup';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute } from '@/navigation/types/navigation';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    wrapper: {
      backgroundColor: theme.colors.surfaceContainerLow,
      paddingBottom: insets.bottom,
      flex: 1,
      paddingHorizontal: 16,
      paddingVertical: 16,
      alignItems: 'center',
      flexDirection: 'column',
      gap: 16
    },
    text: {
      textAlign: 'center'
    },
    group: {
      marginBottom: 8,
      gap: 16,
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'stretch'
    },
    input: {
      minHeight: 180,
      maxHeight: 180,
      alignSelf: 'stretch',
      marginBottom: 8,
      backgroundColor: theme.colors.surfaceContainerLow
    }
  });

type Props = AppNativeStackScreenProps<typeof AppRoute.GroupRequest>;

export const GroupRequestScreen: FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.request'
  });
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { groupId } = route.params;
  const { data: group } = useGetGroup(groupId);

  const [memberIntroductionText, setMemberIntroductionText] = useState('');

  const joinGroup = useJoinGroup();
  const handleJoinRequest = useCallback(
    () =>
      joinGroup.mutate(
        { groupId, memberIntroductionText: memberIntroductionText.trim() },
        {
          onSuccess: ({ group }) => {
            navigation?.popTo(AppRoute.GroupDescription, {
              groupId: group.id
            });
          }
        }
      ),
    [groupId, memberIntroductionText, joinGroup, navigation]
  );

  return (
    group && (
      <KeyboardAwareScrollView contentContainerStyle={styles.wrapper}>
        <Text variant="headlineLarge" style={styles.text}>
          {t('title')}
        </Text>
        <Text variant="bodyLarge" style={styles.text}>
          {t('description')}
        </Text>
        <View style={styles.group}>
          <ProfileAvatar
            viewSize={72}
            mediaItem={group.logo}
            textFallback={{ shortName: group.shortName }}
            mediaSize={MediaItemSize.SMALL}
          />
          <View
            style={{
              flex: 1,
              flexDirection: 'column'
            }}
          >
            <Text variant="titleLarge" numberOfLines={1} ellipsizeMode="tail">
              {group.name}
            </Text>
            <Text variant="bodyLarge">
              {t('members', {
                count: group.memberCount
              })}
            </Text>
          </View>
        </View>
        <TextInput
          multiline
          mode="outlined"
          accessibilityLabel={t('message')}
          accessibilityHint={t('message')}
          textAlignVertical="top"
          placeholder={t('messageHint')}
          label={t('message')}
          value={memberIntroductionText}
          onChangeText={setMemberIntroductionText}
          // Todo: get value from feature
          maxLength={400}
          style={styles.input}
        />

        <Button
          mode="contained"
          onPress={handleJoinRequest}
          disabled={joinGroup.isPending}
        >
          {t('joinGroup')}
        </Button>
      </KeyboardAwareScrollView>
    )
  );
};
